import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import { Link } from 'react-router-dom';

export default function TrackOrderPage() {
  const { orders, isLoggedIn, setLoginModalOpen } = useStore();
  const [trackingId, setTrackingId] = useState('');
  const [searchedOrder, setSearchedOrder] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = () => {
    if (!trackingId.trim()) return;
    setIsSearching(true);
    setTimeout(() => {
      const order = orders.find(o => o.trackingId === trackingId || o.id === trackingId);
      setSearchedOrder(order || null);
      setIsSearching(false);
    }, 500);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-fuchsia-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-map-pin-user-line text-4xl text-indigo-600"></i>
          </div>
          <h1 className="text-3xl font-display font-bold mb-3">Track Your Order</h1>
          <p className="text-slate-500 mb-6">Please login to track your orders</p>
          <button onClick={() => setLoginModalOpen(true)} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg">
            Login / Sign Up
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="relative mb-10">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-fuchsia-600/10 rounded-3xl blur-3xl -z-10"></div>
        <h1 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-indigo-700 to-fuchsia-700 bg-clip-text text-transparent">Track Order</h1>
        <p className="text-slate-500 mt-2">Enter Order ID or Tracking ID to get real-time status</p>
      </div>

      {/* Search Box */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-8">
        <div className="flex gap-3 items-center">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input 
              type="text" 
              placeholder="e.g., TRK123456 or Order ID"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition"
              value={trackingId}
              onChange={e => setTrackingId(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button 
            onClick={handleSearch}
            disabled={isSearching}
            className="bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white px-8 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition disabled:opacity-50"
          >
            {isSearching ? 'Searching...' : 'Track'}
          </button>
        </div>
      </div>

      {/* Search Result */}
      {searchedOrder && (
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-indigo-100 p-6 shadow-lg animate-fadeIn">
          <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
            <div>
              <p className="text-sm text-slate-500">Order #{searchedOrder.id.slice(-8)}</p>
              <p className="text-xs font-mono text-slate-400">{searchedOrder.trackingId}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              searchedOrder.status === 'Delivered' ? 'bg-green-100 text-green-700' : 
              searchedOrder.status === 'Shipped' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {searchedOrder.status}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-400">Delivery Address</p>
              <p className="text-sm">{searchedOrder.address}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Payment Method</p>
              <p className="text-sm capitalize">{searchedOrder.paymentMethod}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Order Date</p>
              <p className="text-sm">{new Date(searchedOrder.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Estimated Delivery</p>
              <p className="text-sm">{searchedOrder.deliveryEstimate}</p>
            </div>
          </div>
          <Link to={`/tracking/${searchedOrder.id}`} className="inline-flex items-center gap-1 text-indigo-600 font-semibold text-sm hover:underline">
            View Full Tracking Details <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      )}

      {/* Recent Orders for quick tracking */}
      {orders.length > 0 && !searchedOrder && (
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><i className="ri-history-line text-indigo-500"></i> Recent Orders</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {orders.slice(0, 4).map(order => (
              <div key={order.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition group">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-xs text-slate-500">#{order.id.slice(-8)}</p>
                    <p className="text-sm font-semibold mt-1">₹{order.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">{new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                  <button 
                    onClick={() => { setTrackingId(order.trackingId); handleSearch(); }}
                    className="text-indigo-600 text-sm font-medium hover:underline group-hover:translate-x-0.5 transition"
                  >
                    Track <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}